import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Header from './Header';
import Pagination from './Pagination';
import {
  Award,
  TrendingUp,
  Users,
  Eye,
  Download,
  Search,
  Filter,
  DollarSign,
  MapPin,
  Mail,
  CheckCircle,
  Calendar,
  Clock,
  Target,
  Check,
  User,
  Phone
} from 'lucide-react';

const ModalViewPlotsCustomersCurrentPayers = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;

  let business_locationIDMain = localStorage.getItem('business_locationIDMain');

  let userCompanyID = localStorage.getItem('companyID')
  if(userCompanyID === 'Admin'){
   userCompanyID = 1;
  }
  userCompanyID = JSON.parse(userCompanyID)

  const [customersData, setCustomersData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showStatementModal, setShowStatementModal] = useState(false);

  useEffect(() => {
    fetchDataForTablePlotsCustomers();
  }, []);

  useEffect(() => {
    // Filter data whenever search value changes
    if (searchValue.trim() === '') {
      setFilteredData(customersData);
    } else {
      const filtered = customersData.filter(customer =>
        Object.values(customer).some(value =>
          value?.toString().toLowerCase().includes(searchValue.toLowerCase())
        )
      );
      setFilteredData(filtered);
    }
    setCurrentPage(1);
  }, [searchValue, customersData]);

  const fetchDataForTablePlotsCustomers = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('apiType', 'fetchViewPlotsCustomersCurrentPayers');
    formData.append('companyID', userCompanyID);
    formData.append('business_locationIDMain', business_locationIDMain);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setCustomersData(response.data);
      setFilteredData(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching data:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const formatCurrencyTable = (amount) => {
    const number = typeof amount === 'number' ? amount : parseFloat(amount);
    return new Intl.NumberFormat('en-ZM', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2}).format(number);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    const tableRows = [];

    filteredData.forEach((row, index) => {
      tableRows.push([
        index + 1,
        row.businessLocationName,
        row.plotNumber,
        row.plotNumberNew,
        row.name,
        row.dateContractSigned,
        row.lastDatePaid,
        formatCurrencyTable(row.plotPrice),
        formatCurrencyTable(row.plotPricesNew),
        formatCurrencyTable(row.monthlyInstallments),
        formatCurrencyTable(row.totalAmountPaid),
        formatCurrencyTable(row.balance)
      ]);
    });

    doc.autoTable({
      head: [['SN', 'Location', 'Plot #', 'New Plot #', 'Name', 'Contract Date', 'Last Paid', 'Plot Price', 'New Price', 'Monthly', 'Total Paid', 'Balance']],
      body: tableRows,
      margin: { top: 20 },
      styles: {
        fontSize: 7,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [52, 152, 219],
        textColor: 255,
        fontStyle: 'bold'
      }
    });

    doc.save('current-payers-report.pdf');
  };

  const calculateTotals = (data) => {
    const totals = data.reduce((acc, customer) => {
      acc.totalAmountPaid += parseFloat(customer.totalAmountPaid) || 0;
      acc.totalBalance += parseFloat(customer.balance) || 0;
      acc.totalPlotValue += parseFloat(customer.plotPricesNew) || 0;
      acc.totalCustomers += 1;
      acc.totalMonthly += parseFloat(customer.monthlyInstallments) || 0;
      return acc;
    }, { 
      totalAmountPaid: 0, 
      totalBalance: 0, 
      totalPlotValue: 0,
      totalCustomers: 0,
      totalMonthly: 0
    });
    
    return totals;
  };

  const calculatePaymentProgress = (customer) => {
    const plotPrice = parseFloat(customer.plotPricesNew) || 0;
    const totalPaid = parseFloat(customer.totalAmountPaid) || 0;
    
    if (plotPrice === 0) return 0;
    return Math.min(100, (totalPaid / plotPrice) * 100);
  };

  const handleViewStatement = (customer) => {
    setSelectedCustomer(customer);
    setShowStatementModal(true);
  };

  const sendLoginCredentials = async (customerId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'sendEmailPlotsLoginCredentials');
      formData.append('id', customerId);
      
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      alert('Login credentials sent successfully');
    } catch (error) {
      alert('Error sending login credentials');
    }
  };

  const confirmForPaymentReminders = async (customerId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'comfirmClientAvailability');
      formData.append('customerID', customerId);
      formData.append('customerType', 'Plots');
      
      const response = await axios.post(`${server_app_api_php}/server/payment_remindersEmailList.php`, formData);
      alert('Confirmed for automated payment reminders');
    } catch (error) {
      alert('Error confirming for payment reminders');
    }
  };

  // Calculate pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const totals = calculateTotals(customersData);
  const filteredTotals = calculateTotals(filteredData);

  return (
    <>
      <Header />
      <main className="lg:ml-64 pt-16 min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Up-to-Date Payers</h1>
                    <p className="text-gray-600">Customers who are current with their payments</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleExportPDF}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export PDF</span>
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Current Payers</p>
                      <p className="text-2xl font-bold text-gray-900">{totals.totalCustomers}</p>
                      <p className="text-xs text-blue-600 mt-1">All payments up to date ✓</p>
                    </div>
                    <Award className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Plot Value</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalPlotValue)}</p>
                      <p className="text-xs text-gray-500 mt-1">Total Monthly: {formatCurrency(totals.totalMonthly)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Payments Received</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalAmountPaid)}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Target className="w-4 h-4 text-blue-500" />
                        <span className="text-xs text-blue-600">
                          {formatCurrency(totals.totalBalance)} remaining
                        </span>
                      </div>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search up-to-date payers..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white w-64"
                      />
                    </div>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                      className="px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5 entries</option>
                      <option value="10">10 entries</option>
                      <option value="25">25 entries</option>
                      <option value="50">50 entries</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1 text-blue-600">
                      <Award className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        {filteredData.length} up-to-date payers
                        {searchValue && ` (filtered)`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="mt-4 text-gray-500">Loading up-to-date payers data...</p>
                  </div>
                ) : (
                  <>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-blue-500 to-cyan-600">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">SN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            Location
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Plot #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Last Paid
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                           
                            Plot Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Monthly</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Total Paid</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Balance</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Progress</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentRows.map((customer, index) => {
                          const progress = calculatePaymentProgress(customer);
                          return (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{indexOfFirstRow + index + 1}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                                  {customer.businessLocationName}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-blue-600">{customer.plotNumber}</div>
                                  <div className="text-xs text-gray-500">New: {customer.plotNumberNew}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                                  <div className="text-xs text-gray-500">{customer.customersEmail}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <Calendar className="w-4 h-4 text-green-500 mr-2" />
                                  {customer.lastDatePaid}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatCurrency(customer.plotPricesNew)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(customer.monthlyInstallments)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                                {formatCurrency(customer.totalAmountPaid)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  parseFloat(customer.balance) > 0 ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  {formatCurrency(customer.balance)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        progress >= 100 ? 'bg-green-500' : 
                                        progress >= 75 ? 'bg-blue-500' : 
                                        progress >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${progress}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs font-medium">{progress.toFixed(1)}%</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewStatement(customer)}
                                    className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                  >
                                    <Eye className="w-4 h-4" />
                                    <span>View</span>
                                  </button>
                                  <button
                                    onClick={() => sendLoginCredentials(customer.customersID)}
                                    className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                  >
                                    <Mail className="w-4 h-4" />
                                    <span>Login</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                            Filtered Totals:
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.totalMonthly)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.totalAmountPaid)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(filteredTotals.totalBalance)}
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>

                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      totalItems={filteredData.length}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Statement Modal */}
        {showStatementModal && selectedCustomer && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              
              <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-white">Customer Statement - Up-to-Date Payer</h3>
                    <button
                      onClick={() => setShowStatementModal(false)}
                      className="text-white hover:text-gray-200"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                
                <div className="px-6 py-6">
                  {/* Customer Information */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Customer Name</p>
                        <p className="font-medium text-gray-900">{selectedCustomer.name}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Email Address</p>
                        <p className="font-medium text-gray-900">{selectedCustomer.customersEmail}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Phone Number</p>
                        <p className="font-medium text-gray-900">{selectedCustomer.customersPhone}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Plot Number</p>
                        <p className="font-medium text-gray-900">{selectedCustomer.plotNumber}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Financial Summary */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Financial Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-xl">
                        <p className="text-sm text-green-600">Plot Price</p>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(selectedCustomer.plotPricesNew)}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <p className="text-sm text-blue-600">Total Paid</p>
                        <p className="text-2xl font-bold text-blue-700">{formatCurrency(selectedCustomer.totalAmountPaid)}</p>
                      </div>
                      <div className="p-4 bg-blue-100 rounded-xl">
                        <p className="text-sm text-blue-800">Remaining Balance</p>
                        <p className="text-2xl font-bold text-blue-900">{formatCurrency(selectedCustomer.balance)}</p>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-6">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Payment Progress</span>
                        <span>{calculatePaymentProgress(selectedCustomer).toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-cyan-600 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${calculatePaymentProgress(selectedCustomer)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl">
                    <div className="flex items-center space-x-3">
                      <Check className="w-6 h-6 text-green-500" />
                      <div>
                        <h4 className="text-lg font-medium text-green-800">Payment Status: Up-to-Date</h4>
                        <p className="text-green-600">
                          Last payment: {selectedCustomer.lastDatePaid} • 
                          Next payment due: Within terms
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => confirmForPaymentReminders(selectedCustomer.customersID)}
                      className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4 inline mr-2" />
                      Confirm for Reminders
                    </button>
                    <button
                      onClick={() => setShowStatementModal(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default ModalViewPlotsCustomersCurrentPayers;